import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Coins } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  symbol: "",
  asset_class_id: "ac_equity",
  ex_date: new Date().toISOString().slice(0, 10),
  pay_date: "",
  per_share: "",
  quantity: "",
  tax_withheld: "0",
  notes: "",
};

function DividendsCard({ currency, assetClasses = [], refreshKey = 0, onChanged }) {
  const [data, setData] = useState({ dividends: [], total_gross: 0, total_tax_withheld: 0, total_net: 0, by_symbol: {} });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    fetch(`${API}/dividends?currency=${currency}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(j => j && setData(j))
      .finally(() => setLoading(false));
  }, [currency]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll, refreshKey]);

  const fmt = (n) => `${currency} ${Number(n || 0).toFixed(2)}`;

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (d) => {
    setForm({
      symbol: d.symbol,
      asset_class_id: d.asset_class_id,
      ex_date: d.ex_date,
      pay_date: d.pay_date || "",
      per_share: String(d.per_share),
      quantity: String(d.quantity),
      tax_withheld: String(d.tax_withheld || 0),
      notes: d.notes || "",
    });
    setEditingId(d.dividend_id);
    setOpen(true);
  };

  const validate = () => {
    if (!form.symbol.trim()) return "Symbol is required";
    if (!form.ex_date) return "Ex-date is required";
    const ps = parseFloat(form.per_share);
    const q = parseFloat(form.quantity);
    if (!(ps > 0)) return "Per-share dividend must be > 0";
    if (!(q > 0)) return "Quantity must be > 0";
    const tw = parseFloat(form.tax_withheld || "0");
    if (!(tw >= 0)) return "Tax withheld must be ≥ 0";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        symbol: form.symbol.trim(),
        asset_class_id: form.asset_class_id,
        currency,
        ex_date: form.ex_date,
        pay_date: form.pay_date || null,
        per_share: parseFloat(form.per_share),
        quantity: parseFloat(form.quantity),
        tax_withheld: parseFloat(form.tax_withheld || "0"),
        notes: form.notes || null,
      };
      const url = editingId ? `${API}/dividends/${editingId}` : `${API}/dividends`;
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editingId ? "Dividend updated" : "Dividend added");
        setOpen(false);
        fetchAll();
        if (onChanged) onChanged();
      } else {
        const text = await res.text();
        toast.error(text || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this dividend record?")) return;
    try {
      const res = await fetch(`${API}/dividends/${id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("Deleted");
        fetchAll();
        if (onChanged) onChanged();
      } else {
        toast.error("Delete failed");
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Card data-testid="dividends-card" className="border border-border mb-8">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold inline-flex items-center gap-2">
              <Coins className="h-5 w-5" strokeWidth={1.5} />
              Dividends
            </CardTitle>
            <CardDescription>
              Track cash dividends received per holding. Net = Gross − Tax Withheld.
            </CardDescription>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-dividend-btn" size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                Add Dividend
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="dividend-dialog" className="sm:max-w-[520px]">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Dividend" : "Add Dividend"}</DialogTitle>
                <DialogDescription>
                  Record a cash dividend payout for one of your holdings ({currency}).
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="dv-symbol">Symbol</Label>
                  <Input
                    id="dv-symbol"
                    data-testid="dv-symbol"
                    value={form.symbol}
                    onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                    placeholder="e.g. AAPL"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Asset Class</Label>
                  <Select value={form.asset_class_id} onValueChange={(v) => setForm({ ...form, asset_class_id: v })}>
                    <SelectTrigger data-testid="dv-class"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {assetClasses.map(ac => (
                        <SelectItem key={ac.asset_class_id} value={ac.asset_class_id}>{ac.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dv-exdate">Ex-Date</Label>
                  <Input
                    id="dv-exdate"
                    data-testid="dv-exdate"
                    type="date"
                    value={form.ex_date}
                    onChange={(e) => setForm({ ...form, ex_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dv-paydate">Pay Date (optional)</Label>
                  <Input
                    id="dv-paydate"
                    data-testid="dv-paydate"
                    type="date"
                    value={form.pay_date}
                    onChange={(e) => setForm({ ...form, pay_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dv-per-share">Per Share ({currency})</Label>
                  <Input
                    id="dv-per-share"
                    data-testid="dv-per-share"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.per_share}
                    onChange={(e) => setForm({ ...form, per_share: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dv-qty">Quantity</Label>
                  <Input
                    id="dv-qty"
                    data-testid="dv-qty"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="dv-tax">Tax Withheld ({currency})</Label>
                  <Input
                    id="dv-tax"
                    data-testid="dv-tax"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.tax_withheld}
                    onChange={(e) => setForm({ ...form, tax_withheld: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="dv-notes">Notes (optional)</Label>
                  <Input
                    id="dv-notes"
                    data-testid="dv-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                <Button data-testid="save-dividend-btn" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : (editingId ? "Update" : "Add")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0 sm:p-6 sm:pt-0">
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : data.dividends.length === 0 ? (
          <div className="p-6 text-center py-12 text-muted-foreground text-sm">
            No dividends recorded yet. Click <strong>Add Dividend</strong> to log your first payout.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 px-6 pt-2 pb-4 border-b border-border">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total Gross</div>
                <div data-testid="div-total-gross" className="text-lg font-semibold tabular-nums">{fmt(data.total_gross)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tax Withheld</div>
                <div data-testid="div-total-tax" className="text-lg font-semibold tabular-nums">{fmt(data.total_tax_withheld)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Net Received</div>
                <div data-testid="div-total-net" className="text-lg font-semibold tabular-nums text-success">{fmt(data.total_net)}</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="py-3 px-4">Ex-Date</th>
                    <th className="py-3 px-4">Pay Date</th>
                    <th className="py-3 px-4">Symbol</th>
                    <th className="py-3 px-4 text-right">Qty</th>
                    <th className="py-3 px-4 text-right">Per Share</th>
                    <th className="py-3 px-4 text-right">Gross</th>
                    <th className="py-3 px-4 text-right">Tax</th>
                    <th className="py-3 px-4 text-right">Net</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dividends.map(d => (
                    <tr key={d.dividend_id} data-testid={`dividend-${d.dividend_id}`} className="border-b border-border hover:bg-slate-50">
                      <td className="py-3 px-4 tabular-nums">{d.ex_date}</td>
                      <td className="py-3 px-4 tabular-nums text-muted-foreground">{d.pay_date || "—"}</td>
                      <td className="py-3 px-4 font-semibold">{d.symbol}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{d.quantity}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(d.per_share)}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmt(d.gross_amount)}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-muted-foreground">{fmt(d.tax_withheld)}</td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold text-success">{fmt(d.net_amount)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          data-testid={`edit-dividend-${d.dividend_id}`}
                          onClick={() => openEdit(d)}
                          className="text-muted-foreground hover:text-accent transition-colors mr-2"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4 inline" strokeWidth={1.5} />
                        </button>
                        <button
                          data-testid={`delete-dividend-${d.dividend_id}`}
                          onClick={() => handleDelete(d.dividend_id)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 inline" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {Object.keys(data.by_symbol).length > 0 && (
              <div className="px-6 py-4 flex flex-wrap gap-2 border-t border-border">
                <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground mr-2">By Symbol:</span>
                {Object.entries(data.by_symbol).map(([sym, net]) => (
                  <Badge key={sym} variant="outline" className="text-xs font-normal tabular-nums">
                    {sym}: {fmt(net)}
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default DividendsCard;
